import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { ErrorText, LoadingIndicator } from "@/components";
import { FormTextAreaInput } from "@/components/form/FormTextAreaInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { jobPauseTypeToLabel } from "@/lib/constants";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { JobPauseType } from "@/types";

const schema = z.object({
  pauseReason: z.string().trim().min(1, "A pause reason is required."),
});

// Verb forms per pause type, for the dialog copy. Both flavors behave
// identically — only the wording changes.
const pauseTypeToVerb: Record<JobPauseType, string> = {
  [JobPauseType.COVERED]: "Cover",
  [JobPauseType.PAUSED]: "Pause",
};

const pauseTypeToPastVerb: Record<JobPauseType, string> = {
  [JobPauseType.COVERED]: "covered",
  [JobPauseType.PAUSED]: "paused",
};

export const JobPauseConfirmationDialog = ({
  isError,
  isLoading,
  isRequest,
  onConfirm,
  onOpenChange,
  open,
  pauseType,
}: {
  isError?: boolean;
  isLoading?: boolean;
  isRequest?: boolean;
  onConfirm: (pauseReason: string) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  pauseType: JobPauseType;
}) => {
  /* React Hooks */
  const form = useForm<z.infer<typeof schema>>({
    defaultValues: { pauseReason: "" },
    resolver: zodResolver(schema),
  });

  // Don't carry a stale reason into the next pause. Keyed off the prop, not
  // onOpenChange, because the success path closes the dialog programmatically
  // and Radix only fires onOpenChange for user-initiated closes.
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [form, open]);

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof schema>) => {
    if (isLoading) {
      return;
    }
    mixpanelUtils.trackFormSubmission(
      isRequest
        ? `Request ${jobPauseTypeToLabel[pauseType]} Job`
        : `${jobPauseTypeToLabel[pauseType]} Job`,
      getScreenFromPathname(window.location.pathname),
    );
    onConfirm(data.pauseReason);
  };

  /* Render */
  const render = () => {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent
          className="flex flex-col gap-6"
          onClick={(e) => {
            e.stopPropagation();
          }}
          preventOverlayClickCapturePropagation
        >
          <DialogHeader className="flex flex-col gap-4">
            <DialogTitle className="text-2xl font-semibold text-slate-700">
              {isRequest
                ? `Request that this job be ${pauseTypeToPastVerb[pauseType]}?`
                : `${pauseTypeToVerb[pauseType]} this job?`}
            </DialogTitle>
            <DialogDescription className="!m-0 text-base font-medium text-slate-700">
              Worker app still shows the job as active, but Sally and AI
              scheduling agent stop initial outreach.
              {isRequest
                ? " Corbin must approve before these effects take place."
                : null}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              className="flex flex-col gap-6"
              onSubmit={(e) => {
                void form.handleSubmit(handleSubmit)(e);
              }}
              // The reason is a textarea, so plain Enter has to stay a newline.
              onKeyDown={(e) => {
                if (e.metaKey && e.key === "Enter") {
                  e.preventDefault();
                  void form.handleSubmit(handleSubmit)();
                }
              }}
            >
              <FormTextAreaInput
                fieldName="pauseReason"
                form={form}
                inputClassName="resize-none"
                placeholder={
                  isRequest
                    ? `Why should this job be ${pauseTypeToPastVerb[pauseType]}?`
                    : `Why is this job being ${pauseTypeToPastVerb[pauseType]}?`
                }
              />
              <DialogFooter>
                <Button
                  className="rounded-full"
                  disabled={isLoading}
                  onClick={() => {
                    onOpenChange(false);
                  }}
                  type="button"
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  className="rounded-full"
                  disabled={isLoading}
                  type="submit"
                >
                  {isLoading ? (
                    <LoadingIndicator gearColor="white" variant="small" />
                  ) : (
                    <span>
                      {isRequest
                        ? "Send request to Corbin"
                        : pauseTypeToVerb[pauseType]}
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
          {isError && (
            <ErrorText
              errorMessage={
                isRequest
                  ? "Failed to send the request. Please try again."
                  : "Failed to pause the job. Please try again."
              }
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
